import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Plus, Trash2, Save, Loader2, X, Upload, GripVertical, Pencil } from "lucide-react";

/**
 * Reusable admin CRUD panel.
 *
 * Props:
 *   title, description
 *   endpoint - e.g. "events" (uses /api/{endpoint} public + /api/admin/{endpoint})
 *   fields - array of {name, label, type, placeholder, options?, required?, rows?}
 *     type: "text" | "textarea" | "image" | "select" | "date" | "time" | "number"
 *   emptyItem - default object shape
 *   itemLabel - fn(item) => string  (title for list rows)
 *   itemSubtitle - fn(item) => string
 */
export default function AdminCRUD({ title, description, endpoint, fields, emptyItem, itemLabel, itemSubtitle, testid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // item being edited, or emptyItem for new
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${endpoint}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [endpoint]);

  const startNew = () => setEditing({ ...emptyItem });
  const startEdit = (item) => setEditing({ ...item });
  const cancel = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      if (payload.id) {
        const { id, ...rest } = payload;
        await api.put(`/admin/${endpoint}/${id}`, rest);
        toast.success("Saved.");
      } else {
        await api.post(`/admin/${endpoint}`, payload);
        toast.success("Created.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const moveOrder = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const a = { ...items[idx] };
    const b = { ...items[target] };
    const aOrder = a.order ?? idx;
    const bOrder = b.order ?? target;
    try {
      await api.post(`/admin/${endpoint}/reorder`, [
        { id: a.id, order: bOrder },
        { id: b.id, order: aOrder },
      ]);
      // simple re-sort locally
      const copy = [...items];
      copy[idx] = b; copy[target] = a;
      setItems(copy);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={testid || `admin-${endpoint}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[var(--rts-ink)]">{title}</h1>
          {description && <p className="text-sm text-[var(--rts-muted)] mt-1">{description}</p>}
        </div>
        <button
          onClick={startNew}
          data-testid={`${endpoint}-new-btn`}
          className="inline-flex items-center gap-2 bg-[var(--rts-blue-deep)] hover:bg-[var(--rts-blue)] text-white font-bold px-5 py-2.5 rounded-full min-h-[44px]"
        >
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--rts-blue-deep)]" /></div>
      ) : items.length === 0 && !editing ? (
        <div className="bg-white border-2 border-dashed border-[var(--rts-blue-pale)] rounded-2xl p-10 text-center text-[var(--rts-muted)]">
          Nothing here yet. Click <strong>New</strong> above to create your first {title.toLowerCase().replace(/s$/, "")}.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[var(--rts-blue-pale)] divide-y divide-[var(--rts-blue-pale)] overflow-hidden">
          {items.map((item, idx) => {
            const imageField = fields.find((f) => f.type === "image");
            const imgPath = imageField ? item[imageField.name] : "";
            return (
              <div key={item.id} className="p-4 flex items-center gap-4" data-testid={`${endpoint}-row-${idx}`}>
                <div className="flex flex-col">
                  <button
                    onClick={() => moveOrder(idx, -1)}
                    disabled={idx === 0}
                    aria-label="Move up"
                    className="p-1 text-[var(--rts-muted)] hover:text-[var(--rts-ink)] disabled:opacity-30"
                  >▲</button>
                  <GripVertical className="w-4 h-4 text-[var(--rts-muted)]/50 self-center" />
                  <button
                    onClick={() => moveOrder(idx, 1)}
                    disabled={idx === items.length - 1}
                    aria-label="Move down"
                    className="p-1 text-[var(--rts-muted)] hover:text-[var(--rts-ink)] disabled:opacity-30"
                  >▼</button>
                </div>
                {imgPath && (
                  <img src={fileUrl(imgPath)} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[var(--rts-ink)] truncate">{itemLabel(item)}</div>
                  {itemSubtitle && <div className="text-xs text-[var(--rts-muted)] truncate">{itemSubtitle(item)}</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => startEdit(item)}
                    aria-label="Edit"
                    data-testid={`${endpoint}-edit-${idx}`}
                    className="w-10 h-10 grid place-items-center rounded-lg bg-[var(--rts-blue-pale)] hover:bg-[var(--rts-blue)] hover:text-white text-[var(--rts-blue-deep)]"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(item.id)}
                    aria-label="Delete"
                    data-testid={`${endpoint}-delete-${idx}`}
                    className="w-10 h-10 grid place-items-center rounded-lg bg-red-50 hover:bg-[var(--rts-red)] hover:text-white text-[var(--rts-red)]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <EditModal
          fields={fields}
          value={editing}
          onChange={setEditing}
          onSave={save}
          onCancel={cancel}
          saving={saving}
          isNew={!editing.id}
          testidPrefix={endpoint}
        />
      )}
    </div>
  );
}

function EditModal({ fields, value, onChange, onSave, onCancel, saving, isNew, testidPrefix }) {
  const update = (name, v) => onChange({ ...value, [name]: v });
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid={`${testidPrefix}-edit-modal`}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-display text-xl text-[var(--rts-ink)]">{isNew ? "New" : "Edit"}</h2>
          <button onClick={onCancel} aria-label="Close" className="w-10 h-10 grid place-items-center rounded-lg hover:bg-[var(--rts-blue-pale)]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {fields.map((f) => (
            <Field key={f.name} field={f} value={value[f.name] ?? ""} onChange={(v) => update(f.name, v)} testidPrefix={testidPrefix} />
          ))}
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button onClick={onCancel} className="px-5 py-2.5 rounded-full font-semibold text-[var(--rts-ink)] hover:bg-[var(--rts-blue-pale)] min-h-[44px]">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid={`${testidPrefix}-save-btn`}
            className="inline-flex items-center gap-2 bg-[var(--rts-blue-deep)] hover:bg-[var(--rts-blue)] text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-60 min-h-[44px]"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ field, value, onChange, testidPrefix }) {
  const cls = "w-full border-2 border-[var(--rts-blue-pale)] focus:border-[var(--rts-blue)] rounded-xl px-3 py-2.5 outline-none min-h-[44px]";
  const testId = `${testidPrefix}-field-${field.name}`;
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-[var(--rts-ink)] mb-1">
        {field.label}{field.required && <span className="text-[var(--rts-red)]"> *</span>}
      </span>
      {field.type === "textarea" ? (
        <textarea rows={field.rows || 4} value={value} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className={cls + " resize-none"} data-testid={testId} />
      ) : field.type === "image" ? (
        <ImageField value={value} onChange={onChange} testId={testId} />
      ) : field.type === "select" ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={testId}>
          <option value="">Select...</option>
          {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={field.type || "text"} value={value} onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)} placeholder={field.placeholder} className={cls} data-testid={testId} />
      )}
      {field.help && <p className="text-xs text-[var(--rts-muted)] mt-1">{field.help}</p>}
    </label>
  );
}

function ImageField({ value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap">
        {value ? (
          <img src={fileUrl(value)} alt="" className="w-24 h-24 object-cover rounded-xl ring-2 ring-[var(--rts-blue-pale)]" />
        ) : (
          <div className="w-24 h-24 rounded-xl bg-[var(--rts-blue-pale)] grid place-items-center text-[var(--rts-muted)] text-xs">No image</div>
        )}
        <label className="inline-flex items-center gap-2 bg-[var(--rts-blue-pale)] hover:bg-[var(--rts-blue)] hover:text-white text-[var(--rts-blue-deep)] cursor-pointer px-4 py-2.5 rounded-full font-bold text-sm min-h-[44px]">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading..." : (value ? "Replace" : "Upload")}
          <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" data-testid={testId} onChange={(e) => doUpload(e.target.files?.[0])} />
        </label>
        {value && (
          <button onClick={() => onChange("")} className="text-xs text-[var(--rts-red)] hover:underline">Remove</button>
        )}
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Or paste an image URL / path"
        className="mt-2 w-full text-xs border border-[var(--rts-blue-pale)] rounded-lg px-3 py-2 outline-none focus:border-[var(--rts-blue)]"
      />
    </div>
  );
}
